import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useLocalStorage } from "./useLocalStorage";
import { useToast } from "./use-toast";

// Migrate localStorage keys from old prefix to new prefix (one-time)
function migrateLocalStorageKeys() {
  const migrations = [
    { from: "doterra-favorites", to: "guia-essencial-favorites" },
    { from: "doterra-cabinet", to: "guia-essencial-cabinet" },
    { from: "guia-doterra-theme", to: "guia-essencial-theme" },
    { from: "guia-natural-favorites", to: "guia-essencial-favorites" },
    { from: "guia-natural-cabinet", to: "guia-essencial-cabinet" },
    { from: "guia-natural-theme", to: "guia-essencial-theme" },
  ];

  migrations.forEach(({ from, to }) => {
    const oldValue = localStorage.getItem(from);
    if (oldValue && !localStorage.getItem(to)) {
      localStorage.setItem(to, oldValue);
      localStorage.removeItem(from);
      console.log(`Migrated localStorage: ${from} -> ${to}`);
    }
  });
}

export function useRealtimeSync() {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  // Essential oil favorites (backward compatible)
  const [favorites, setFavorites] = useState<string[]>([]);
  // Product-type favorites
  const [favoriteVegetableOils, setFavoriteVegetableOils] = useState<string[]>([]);
  const [favoriteNeutralBases, setFavoriteNeutralBases] = useState<string[]>([]);
  const [favoriteSupplies, setFavoriteSupplies] = useState<string[]>([]);
  
  // Essential oil cabinet (backward compatible)
  const [cabinetOils, setCabinetOils] = useState<string[]>([]);
  // Product-type cabinet state
  const [cabinetVegetableOils, setCabinetVegetableOils] = useState<string[]>([]);
  const [cabinetNeutralBases, setCabinetNeutralBases] = useState<string[]>([]);
  const [cabinetSupplies, setCabinetSupplies] = useState<string[]>([]);
  
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const [localFavorites, setLocalFavorites] = useLocalStorage<string[]>("guia-essencial-favorites", []);
  const [localCabinet, setLocalCabinet] = useLocalStorage<string[]>("guia-essencial-cabinet", []);
  
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const hasSyncedRef = useRef(false);
  
  const localFavoritesRef = useRef(localFavorites);
  const localCabinetRef = useRef(localCabinet);
  
  useEffect(() => { migrateLocalStorageKeys(); }, []);

  useEffect(() => { localFavoritesRef.current = localFavorites; }, [localFavorites]);
  useEffect(() => { localCabinetRef.current = localCabinet; }, [localCabinet]);

  // Helper: get favorite setter for a given item_type
  const getFavoriteSetter = useCallback((itemType: string) => {
    switch (itemType) {
      case "vegetable_oil": return setFavoriteVegetableOils;
      case "neutral_base": return setFavoriteNeutralBases;
      case "supply": return setFavoriteSupplies;
      default: return setFavorites;
    }
  }, []);

  // Helper: get cabinet setter for a given item_type
  const getCabinetSetter = useCallback((itemType: string) => {
    switch (itemType) {
      case "vegetable_oil": return setCabinetVegetableOils;
      case "neutral_base": return setCabinetNeutralBases;
      case "supply": return setCabinetSupplies;
      default: return setCabinetOils;
    }
  }, []);

  // Fetch initial data from database
  const fetchInitialData = useCallback(async (userId: string) => {
    setIsLoading(true);
    
    try {
      const [
        favEORes, favVORes, favNBRes, favSPRes,
        cabEORes, cabVORes, cabNBRes, cabSPRes,
      ] = await Promise.all([
        supabase.from("user_favorites").select("oil_id").eq("user_id", userId).eq("item_type", "essential_oil"),
        supabase.from("user_favorites").select("oil_id").eq("user_id", userId).eq("item_type", "vegetable_oil"),
        supabase.from("user_favorites").select("oil_id").eq("user_id", userId).eq("item_type", "neutral_base"),
        supabase.from("user_favorites").select("oil_id").eq("user_id", userId).eq("item_type", "supply"),
        supabase.from("user_cabinet").select("oil_id").eq("user_id", userId).eq("item_type", "essential_oil"),
        supabase.from("user_cabinet").select("oil_id").eq("user_id", userId).eq("item_type", "vegetable_oil"),
        supabase.from("user_cabinet").select("oil_id").eq("user_id", userId).eq("item_type", "neutral_base"),
        supabase.from("user_cabinet").select("oil_id").eq("user_id", userId).eq("item_type", "supply"),
      ]);

      if (favEORes.data) setFavorites(favEORes.data.map((f) => f.oil_id));
      if (favVORes.data) setFavoriteVegetableOils(favVORes.data.map((f) => f.oil_id));
      if (favNBRes.data) setFavoriteNeutralBases(favNBRes.data.map((f) => f.oil_id));
      if (favSPRes.data) setFavoriteSupplies(favSPRes.data.map((f) => f.oil_id));
      if (cabEORes.data) setCabinetOils(cabEORes.data.map((c) => c.oil_id));
      if (cabVORes.data) setCabinetVegetableOils(cabVORes.data.map((c) => c.oil_id));
      if (cabNBRes.data) setCabinetNeutralBases(cabNBRes.data.map((c) => c.oil_id));
      if (cabSPRes.data) setCabinetSupplies(cabSPRes.data.map((c) => c.oil_id));
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sync local data to cloud when user logs in
  const syncLocalToCloud = useCallback(async (userId: string) => {
    const localFavs = localFavoritesRef.current;
    const localCab = localCabinetRef.current;

    for (const oilId of localFavs) {
      await supabase
        .from("user_favorites")
        .upsert({ user_id: userId, oil_id: oilId, item_type: "essential_oil" }, { onConflict: "user_id,oil_id,item_type" });
    }

    for (const oilId of localCab) {
      await supabase
        .from("user_cabinet")
        .upsert({ user_id: userId, oil_id: oilId, item_type: "essential_oil" }, { onConflict: "user_id,oil_id,item_type" });
    }
  }, []);

  // Handle initial sync once when user logs in
  useEffect(() => {
    if (!user || hasSyncedRef.current) return;
    const init = async () => {
      hasSyncedRef.current = true;
      await syncLocalToCloud(user.id);
      await fetchInitialData(user.id);
    };
    init();
  }, [user, syncLocalToCloud, fetchInitialData]);

  // Reset sync flag when user logs out
  useEffect(() => {
    if (!user) { hasSyncedRef.current = false; }
  }, [user]);

  // Handle local data when not authenticated
  useEffect(() => {
    if (!user) {
      setFavorites(localFavoritesRef.current);
      setCabinetOils(localCabinetRef.current);
      setFavoriteVegetableOils([]);
      setFavoriteNeutralBases([]);
      setFavoriteSupplies([]);
      setCabinetVegetableOils([]);
      setCabinetNeutralBases([]);
      setCabinetSupplies([]);
      setIsLoading(false);
      setIsConnected(false);
    }
  }, [user]);

  // Setup realtime subscriptions
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel(`user-sync-${user.id}`);
    
    channel
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "user_favorites", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const oilId = payload.new.oil_id as string;
          const itemType = (payload.new.item_type as string) || "essential_oil";
          const setter = getFavoriteSetter(itemType);
          setter((prev) => prev.includes(oilId) ? prev : [...prev, oilId]);
        }
      )
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "user_favorites", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const oilId = payload.old.oil_id as string;
          const itemType = (payload.old.item_type as string) || "essential_oil";
          const setter = getFavoriteSetter(itemType);
          setter((prev) => prev.filter((id) => id !== oilId));
        }
      )
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "user_cabinet", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const oilId = payload.new.oil_id as string;
          const itemType = (payload.new.item_type as string) || "essential_oil";
          const setter = getCabinetSetter(itemType);
          setter((prev) => prev.includes(oilId) ? prev : [...prev, oilId]);
        }
      )
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "user_cabinet", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const oilId = payload.old.oil_id as string;
          const itemType = (payload.old.item_type as string) || "essential_oil";
          const setter = getCabinetSetter(itemType);
          setter((prev) => prev.filter((id) => id !== oilId));
        }
      )
      .subscribe((status, err) => {
        if (status === "SUBSCRIBED") setIsConnected(true);
        else if (status === "CLOSED") setIsConnected(false);
        else if (status === "CHANNEL_ERROR") { console.warn("Realtime: erro no canal", err?.message); setIsConnected(false); }
        else if (status === "TIMED_OUT") setIsConnected(false);
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, getCabinetSetter, getFavoriteSetter]);

  // Add favorite (essential oil - backward compatible)
  const addFavorite = useCallback(async (oilId: string) => {
    setFavorites((prev) => [...prev, oilId]);
    setLocalFavorites((prev) => prev.includes(oilId) ? prev : [...prev, oilId]);

    if (!user) return;
    const { error } = await supabase.from("user_favorites").insert({ user_id: user.id, oil_id: oilId, item_type: "essential_oil" });
    if (error && !error.message.includes("duplicate")) {
      console.error("Error adding favorite:", error);
      setFavorites((prev) => prev.filter((id) => id !== oilId));
      setLocalFavorites((prev) => prev.filter((id) => id !== oilId));
      toast({ title: "Erro", description: "Não foi possível adicionar aos favoritos", variant: "destructive" });
    }
  }, [user, setLocalFavorites, toast]);

  // Remove favorite (essential oil - backward compatible)
  const removeFavorite = useCallback(async (oilId: string) => {
    setFavorites((prev) => prev.filter((id) => id !== oilId));
    setLocalFavorites((prev) => prev.filter((id) => id !== oilId));

    if (!user) return;
    const { error } = await supabase.from("user_favorites").delete().eq("user_id", user.id).eq("oil_id", oilId).eq("item_type", "essential_oil");
    if (error) {
      console.error("Error removing favorite:", error);
      setFavorites((prev) => [...prev, oilId]);
      setLocalFavorites((prev) => [...prev, oilId]);
      toast({ title: "Erro", description: "Não foi possível remover dos favoritos", variant: "destructive" });
    }
  }, [user, setLocalFavorites, toast]);

  // Add favorite by product type
  const addFavoriteByType = useCallback(async (itemId: string, itemType: string) => {
    if (itemType === "essential_oil") return addFavorite(itemId);
    
    const setter = getFavoriteSetter(itemType);
    setter((prev) => [...prev, itemId]);

    if (!user) return;
    const { error } = await supabase.from("user_favorites").insert({ user_id: user.id, oil_id: itemId, item_type: itemType });
    if (error && !error.message.includes("duplicate")) {
      console.error("Error adding favorite:", error);
      setter((prev) => prev.filter((id) => id !== itemId));
      toast({ title: "Erro", description: "Não foi possível adicionar aos favoritos", variant: "destructive" });
    }
  }, [user, toast, addFavorite, getFavoriteSetter]);

  // Remove favorite by product type
  const removeFavoriteByType = useCallback(async (itemId: string, itemType: string) => {
    if (itemType === "essential_oil") return removeFavorite(itemId);
    
    const setter = getFavoriteSetter(itemType);
    setter((prev) => prev.filter((id) => id !== itemId));

    if (!user) return;
    const { error } = await supabase.from("user_favorites").delete().eq("user_id", user.id).eq("oil_id", itemId).eq("item_type", itemType);
    if (error) {
      console.error("Error removing favorite:", error);
      setter((prev) => [...prev, itemId]);
      toast({ title: "Erro", description: "Não foi possível remover dos favoritos", variant: "destructive" });
    }
  }, [user, toast, removeFavorite, getFavoriteSetter]);

  // Add to cabinet (essential oil - backward compatible)
  const addToCabinet = useCallback(async (oilId: string) => {
    setCabinetOils((prev) => [...prev, oilId]);
    setLocalCabinet((prev) => prev.includes(oilId) ? prev : [...prev, oilId]);

    if (!user) return;
    const { error } = await supabase.from("user_cabinet").insert({ user_id: user.id, oil_id: oilId, item_type: "essential_oil" });
    if (error && !error.message.includes("duplicate")) {
      console.error("Error adding to cabinet:", error);
      setCabinetOils((prev) => prev.filter((id) => id !== oilId));
      setLocalCabinet((prev) => prev.filter((id) => id !== oilId));
      toast({ title: "Erro", description: "Não foi possível adicionar ao armário", variant: "destructive" });
    }
  }, [user, setLocalCabinet, toast]);

  // Remove from cabinet (essential oil - backward compatible)
  const removeFromCabinet = useCallback(async (oilId: string) => {
    setCabinetOils((prev) => prev.filter((id) => id !== oilId));
    setLocalCabinet((prev) => prev.filter((id) => id !== oilId));

    if (!user) return;
    const { error } = await supabase.from("user_cabinet").delete().eq("user_id", user.id).eq("oil_id", oilId).eq("item_type", "essential_oil");
    if (error) {
      console.error("Error removing from cabinet:", error);
      setCabinetOils((prev) => [...prev, oilId]);
      setLocalCabinet((prev) => [...prev, oilId]);
      toast({ title: "Erro", description: "Não foi possível remover do armário", variant: "destructive" });
    }
  }, [user, setLocalCabinet, toast]);

  // Add to cabinet by product type
  const addToCabinetByType = useCallback(async (itemId: string, itemType: string) => {
    if (itemType === "essential_oil") return addToCabinet(itemId);
    const setter = getCabinetSetter(itemType);
    setter((prev) => [...prev, itemId]);

    if (!user) return;
    const { error } = await supabase.from("user_cabinet").insert({ user_id: user.id, oil_id: itemId, item_type: itemType });
    if (error && !error.message.includes("duplicate")) {
      console.error("Error adding to cabinet:", error);
      setter((prev) => prev.filter((id) => id !== itemId));
      toast({ title: "Erro", description: "Não foi possível adicionar ao armário", variant: "destructive" });
    }
  }, [user, toast, addToCabinet, getCabinetSetter]);

  // Remove from cabinet by product type
  const removeFromCabinetByType = useCallback(async (itemId: string, itemType: string) => {
    if (itemType === "essential_oil") return removeFromCabinet(itemId);
    const setter = getCabinetSetter(itemType);
    setter((prev) => prev.filter((id) => id !== itemId));

    if (!user) return;
    const { error } = await supabase.from("user_cabinet").delete().eq("user_id", user.id).eq("oil_id", itemId).eq("item_type", itemType);
    if (error) {
      console.error("Error removing from cabinet:", error);
      setter((prev) => [...prev, itemId]);
      toast({ title: "Erro", description: "Não foi possível remover do armário", variant: "destructive" });
    }
  }, [user, toast, removeFromCabinet, getCabinetSetter]);

  // Helper: get all favorites for a given type
  const getFavoritesByType = useCallback((itemType: string): string[] => {
    switch (itemType) {
      case "vegetable_oil": return favoriteVegetableOils;
      case "neutral_base": return favoriteNeutralBases;
      case "supply": return favoriteSupplies;
      default: return favorites;
    }
  }, [favorites, favoriteVegetableOils, favoriteNeutralBases, favoriteSupplies]);

  // Helper: get all cabinet items for a given type
  const getCabinetByType = useCallback((itemType: string): string[] => {
    switch (itemType) {
      case "vegetable_oil": return cabinetVegetableOils;
      case "neutral_base": return cabinetNeutralBases;
      case "supply": return cabinetSupplies;
      default: return cabinetOils;
    }
  }, [cabinetOils, cabinetVegetableOils, cabinetNeutralBases, cabinetSupplies]);

  // Reset to local data (used on logout)
  const resetToLocal = useCallback(() => {
    setFavorites(localFavoritesRef.current);
    setCabinetOils(localCabinetRef.current);
    setFavoriteVegetableOils([]);
    setFavoriteNeutralBases([]);
    setFavoriteSupplies([]);
    setCabinetVegetableOils([]);
    setCabinetNeutralBases([]);
    setCabinetSupplies([]);
    setIsConnected(false);
  }, []);

  return {
    favorites,
    favoriteVegetableOils,
    favoriteNeutralBases,
    favoriteSupplies,
    cabinetOils,
    cabinetVegetableOils,
    cabinetNeutralBases,
    cabinetSupplies,
    isConnected,
    isLoading,
    isAuthenticated,
    addFavorite,
    removeFavorite,
    addFavoriteByType,
    removeFavoriteByType,
    getFavoritesByType,
    addToCabinet,
    removeFromCabinet,
    addToCabinetByType,
    removeFromCabinetByType,
    getCabinetByType,
    resetToLocal,
  };
}
